import type { Annotation } from "@/lib/api";

import {
  DEFAULT_FONT_SIZE_REL,
  MIN_FONT_PX,
  TEXT_LINE_HEIGHT,
  fontStack,
} from "./fonts";
import { hasPressure, outlineToSvgPath, strokePath, strokeOutline } from "./ink";
import { inPaintOrder } from "./paint-order";
import {
  absBox,
  absRings,
  absSegment,
  arrowHead,
  boxShapePath,
  dashPattern,
  isBoxShape,
  isSegmentShape,
  normalizeTracks,
  ringsPath,
  shapeStrokeWidth,
  tablePath,
} from "./shapes";

/** Read-only render of the markup layer (Milestone A).
 *
 * Draws saved annotations into an SVG overlay inside a PDF `.pdf-page` div,
 * scaling page-relative [0,1] geometry to the page's current rendered size — so
 * markup tracks zoom/resize automatically. The overlay is `pointer-events:
 * none` (text selection + AI still work underneath) and every annotation is
 * drawn in its own try/catch, so one malformed annotation can never blank the
 * page or take down the reader (the modularity contract, §5 of the plan). */

const SVG_NS = "http://www.w3.org/2000/svg";
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function safeColor(c: string | null | undefined, fallback: string): string {
  return c && HEX.test(c) ? c : fallback;
}

export function drawPageAnnotations(pageEl: HTMLElement, annotations: Annotation[]): void {
  // Idempotent: clear any prior overlay, then rebuild.
  pageEl.querySelector(":scope > .annotation-overlay")?.remove();
  if (!annotations.length) return;

  const w = pageEl.clientWidth;
  const h = pageEl.clientHeight;
  if (!w || !h) return;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.classList.add("annotation-overlay");
  svg.setAttribute("width", String(w));
  svg.setAttribute("height", String(h));
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  svg.style.position = "absolute";
  svg.style.left = "0";
  svg.style.top = "0";
  svg.style.pointerEvents = "none";
  svg.style.zIndex = "3";

  for (const a of inPaintOrder(annotations)) {
    try {
      const el = renderOne(a, w, h);
      if (el) svg.appendChild(el);
    } catch {
      /* isolate a bad annotation — never break the page */
    }
  }
  pageEl.appendChild(svg);
}

function renderOne(a: Annotation, w: number, h: number): SVGElement | null {
  if (a.type === "ink" && a.points && a.points.length >= 1) {
    const color = safeColor(a.color, "#e0b15c");
    const width = Math.max(1, (a.stroke_rel ?? 0.003) * w);
    // A translucent ink stroke is a highlighter; default fully opaque for a pen.
    const opacity = a.opacity ?? 1;
    // A pressure stroke (or a single-point dot) renders as a variable-width
    // filled outline; perfect-freehand draws one point as a round dot. A plain
    // multi-point stroke stays a uniform polyline (unchanged — zero-risk).
    if (hasPressure(a.points)) {
      const abs = a.points.map((p) => ({ x: p.x * w, y: p.y * h, pressure: p.pressure }));
      const d = outlineToSvgPath(strokeOutline(abs, width));
      if (d) {
        const path = document.createElementNS(SVG_NS, "path");
        path.setAttribute("d", d);
        path.setAttribute("fill", color);
        if (opacity < 1) path.setAttribute("opacity", String(opacity));
        return path;
      }
    }
    // A plain (no-pressure) single point can't be a polyline → a round dot.
    const first = a.points[0];
    if (a.points.length < 2 && first) {
      const circle = document.createElementNS(SVG_NS, "circle");
      circle.setAttribute("cx", String(first.x * w));
      circle.setAttribute("cy", String(first.y * h));
      circle.setAttribute("r", String(width / 2));
      circle.setAttribute("fill", color);
      if (opacity < 1) circle.setAttribute("opacity", String(opacity));
      return circle;
    }
    // A plain (no-pressure) multi-point stroke: draw a SMOOTH curve through the
    // samples (quadratic-through-midpoints, Bug C) rather than a straight-segment
    // polyline. A fast drag samples sparsely, so a raw polyline reads as angular
    // with cut-out gaps at its round joins — worse the more you zoom in. The
    // curve rounds every corner at render time; it is O(points) and independent
    // of the decimation "smoothing level".
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute(
      "d",
      strokePath(a.points.map((p) => [p.x * w, p.y * h])),
    );
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", String(width));
    path.setAttribute("stroke-linecap", opacity < 1 ? "butt" : "round");
    path.setAttribute("stroke-linejoin", "round");
    if (opacity < 1) path.setAttribute("opacity", String(opacity));
    return path;
  }

  // A ruled table: the outer box plus every interior line, in the pen's colour
  // and weight. Its column/row fractions are what a drag in select mode edits.
  if (a.type === "table") {
    const box = absBox(a, w, h);
    const src = a as unknown as { cols?: number[] | null; rows?: number[] | null };
    const d = box && tablePath(box, normalizeTracks(src.cols), normalizeTracks(src.rows));
    if (!d) return null;
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", safeColor(a.color, "#e0b15c"));
    path.setAttribute("stroke-width", String(shapeStrokeWidth(a.stroke_rel, w)));
    path.setAttribute("stroke-linecap", "round");
    if ((a.opacity ?? 1) < 1) path.setAttribute("opacity", String(a.opacity));
    return path;
  }

  // A paint-bucket fill: the traced rings as ONE even-odd path, so whatever the
  // fill flowed around stays unpainted. Drawn under nothing — it stops at the
  // ink by construction, so it never hides the strokes that bound it.
  if (a.type === "fill") {
    const rings = absRings(a, w, h);
    const d = rings && ringsPath(rings);
    if (!d) return null;
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", safeColor(a.color, "#e0b15c"));
    path.setAttribute("fill-rule", "evenodd");
    if ((a.opacity ?? 1) < 1) path.setAttribute("opacity", String(a.opacity));
    return path;
  }

  // Drawn shapes — outline-only, in the pen's colour and weight. Geometry comes
  // from shapes.ts, which the Fabric editor also draws from, so a shape does not
  // shift when you enter annotate mode.
  if (isBoxShape(a.type)) {
    const box = absBox(a, w, h);
    const d = box && boxShapePath(a.type, box, a.corner_radius ?? 0);
    if (!d) return null;
    const width = shapeStrokeWidth(a.stroke_rel, w);
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    // A filled shape paints its interior BEHIND its own outline, so the outline
    // stays crisp on top of it.
    path.setAttribute("fill", a.fill_color ? safeColor(a.fill_color, "none") : "none");
    path.setAttribute("stroke", safeColor(a.color, "#e0b15c"));
    path.setAttribute("stroke-width", String(width));
    path.setAttribute("stroke-linejoin", "round");
    if (a.dash) path.setAttribute("stroke-dasharray", dashPattern(width).join(" "));
    if ((a.opacity ?? 1) < 1) path.setAttribute("opacity", String(a.opacity));
    return path;
  }

  if (isSegmentShape(a.type)) {
    const seg = absSegment(a, w, h);
    if (!seg) return null;
    const [from, to] = seg;
    const width = shapeStrokeWidth(a.stroke_rel, w);
    // One path for the shaft plus (for an arrow) its two barbs, so the whole
    // arrow is a single element with one stroke — no seam where they meet.
    let d = `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
    if (a.type === "arrow") {
      for (const barb of arrowHead(from, to, width)) {
        d += ` M ${to.x} ${to.y} L ${barb.x} ${barb.y}`;
      }
    }
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", safeColor(a.color, "#e0b15c"));
    path.setAttribute("stroke-width", String(width));
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    if (a.dash) path.setAttribute("stroke-dasharray", dashPattern(width).join(" "));
    if ((a.opacity ?? 1) < 1) path.setAttribute("opacity", String(a.opacity));
    return path;
  }

  if (a.type === "highlight" && a.rect) {
    const rect = document.createElementNS(SVG_NS, "rect");
    rect.setAttribute("x", String(a.rect.x * w));
    rect.setAttribute("y", String(a.rect.y * h));
    rect.setAttribute("width", String(a.rect.w * w));
    rect.setAttribute("height", String(a.rect.h * h));
    rect.setAttribute("fill", safeColor(a.color, "#e0b15c"));
    rect.setAttribute("opacity", String(a.opacity ?? 0.3));
    return rect;
  }

  if ((a.type === "text" || a.type === "note") && a.rect && a.text) {
    const rect = a.rect;
    const content = a.text;
    const size = Math.max(MIN_FONT_PX, (a.font_size_rel ?? DEFAULT_FONT_SIZE_REL) * h);
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", String(rect.x * w));
    text.setAttribute("y", String(rect.y * h + size));
    text.setAttribute("fill", safeColor(a.color, "#1b1f1a"));
    text.setAttribute("font-size", String(size));
    text.setAttribute("font-family", fontStack(a.font_family));
    // Multi-line: one tspan per line so wrapping is preserved.
    const lines = content.split("\n");
    if (lines.length === 1) {
      text.textContent = content;
    } else {
      lines.forEach((line, i) => {
        const tspan = document.createElementNS(SVG_NS, "tspan");
        tspan.setAttribute("x", String(rect.x * w));
        tspan.setAttribute("dy", i === 0 ? "0" : String(size * TEXT_LINE_HEIGHT));
        tspan.textContent = line || " ";
        text.appendChild(tspan);
      });
    }
    return text;
  }

  return null;
}
